import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Waves, Eye, EyeSlash, ArrowLeft, Sun, Moon, Lightning } from '@phosphor-icons/react';
import { Logo, LogoText } from '../components/Logo';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate(result.data.role === 'admin' ? '/admin' : '/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 border-r p-12 flex-col justify-between relative overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: 'var(--accent-bg)' }} />

        <div className="relative">
          <Link to="/" className="flex items-center gap-3" data-testid="login-logo">
            <Logo size="lg" />
            <span className="font-black text-2xl tracking-tight">index24hrs<span style={{ color: 'var(--accent)' }}>.com</span></span>
          </Link>
        </div>

        <div className="space-y-6 relative">
          <h2 className="text-4xl font-black tracking-tight leading-tight">
            Index URLs<br />
            <span style={{ color: 'var(--accent)' }}>At Lightning Speed</span>
          </h2>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
            Get your content indexed in seconds, not days. Join thousands of users who trust our platform.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-6 border-t" style={{ borderColor: 'var(--border-main)' }}>
            <div>
              <p className="text-2xl font-black">30<span style={{ color: 'var(--accent)' }}>s</span></p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Avg Time</p>
            </div>
            <div>
              <p className="text-2xl font-black">150</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>URLs/Batch</p>
            </div>
            <div>
              <p className="text-2xl font-black" style={{ color: 'var(--accent)' }}>99.9%</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Success</p>
            </div>
          </div>
        </div>

        <p className="text-sm relative" style={{ color: 'var(--text-dimmed)' }}>© 2026 index24hrs.com</p>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 relative">
        {/* Theme toggle */}
        <button onClick={toggleTheme} className="absolute top-4 right-4 p-2 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="login-theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-3" data-testid="login-mobile-logo">
              <Logo size="md" />
              <LogoText size="md" />
            </Link>
          </div>

          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-sm transition-colors mb-6" style={{ color: 'var(--text-secondary)' }} data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
            <h1 className="text-3xl font-black tracking-tight">Welcome back</h1>
            <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 text-sm rounded-lg" data-testid="login-error">{error}</div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Email</label>
              <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" placeholder="you@example.com" required data-testid="login-email-input" />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Password</label>
              <div className="relative">
                <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="input-field pr-12" placeholder="••••••••" required data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="toggle-password">
                  {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="login-submit">
              {loading ? <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--bg-main)', opacity: 0.3, borderTopColor: 'var(--bg-main)' }} /> : <><Lightning weight="bold" className="w-5 h-5" /> Sign In</>}
            </button>

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm font-medium" style={{ color: 'var(--accent)' }} data-testid="forgot-password-link">Forgot password?</Link>
            </div>
          </form>

          <p className="text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
            Don't have an account?{' '}
            <Link to="/register" className="font-medium" style={{ color: 'var(--accent)' }} data-testid="go-to-register">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
